import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import { isActionKind, roleToDecide } from "../../shared/goals/actions";
import { atLeast } from "../../shared/roles";
import type { AuthUser, MeResponse, Role } from "../../shared/types";

interface AuthValue {
  user: AuthUser | null;
  /** Flashsuite's page to take the plan up on, when the business has none on. */
  planAt: string | null;
  /** The business's name, for the sentence the Flashsuite phone app shows in place of that page. */
  businessName: string;
  /** Flashsuite's address and Flashadz's id there, the one source of both for its menu and bar. */
  flashsuite: MeResponse["flashsuite"];
  loading: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Who is signed in and where. Signing out is Flashsuite's menu's Log out (`fs-logout`, docs/INTEGRATION.md
 * section 16): this session ends, then Flashsuite's.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [planAt, setPlanAt] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState("");
  const [flashsuite, setFlashsuite] = useState<MeResponse["flashsuite"]>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((r) => {
        if (!alive) return;
        setUser(r.user);
        setPlanAt(r.planAt);
        setBusinessName(r.businessName);
        setFlashsuite(r.flashsuite);
      })
      .catch(() => alive && setUser(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const logout = () => {
      void api.logout().then(({ to }) => window.location.assign(to));
    };
    document.addEventListener("fs-logout", logout);
    return () => document.removeEventListener("fs-logout", logout);
  }, []);

  const value = useMemo<AuthValue>(() => ({ user, planAt, businessName, flashsuite, loading }), [user, planAt, businessName, flashsuite, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * Whether the person's role in this business may do what `min` may. The server checks every request; the
 * screens only hide what the role cannot use (docs/INTEGRATION.md section 15).
 */
export function useCan(): (min: Role) => boolean {
  const role = useAuth().user?.role;
  return useCallback((min: Role) => !!role && atLeast(role, min), [role]);
}

/** The role that approves or skips a proposal: its kind's (launching, a new live ad, budgets and starting to send are an Admin's). */
export const needRole = (n: { kind: string; payload: unknown }): Role => (isActionKind(n.kind) ? roleToDecide(n.kind, n.payload) : "admin");

/** Whether the person may approve or skip a proposal. */
export function useMayDecide(): (n: { kind: string; payload: unknown }) => boolean {
  const can = useCan();
  return useCallback((n: { kind: string; payload: unknown }) => can(needRole(n)), [can]);
}

/** Its children only for a role that may do what `min` may. */
export function Can({ min, children }: { min: Role; children: ReactNode }) {
  return useCan()(min) ? children : null;
}
